import { Doc } from '@dadohr/shared/domainTypes/document';
import { IUser } from '@dadohr/shared/domainTypes/user';
import styled from '@emotion/styled';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  TextField,
  Typography
} from '@material-ui/core';
import { orderBy } from 'lodash';
import { Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { DialogTitleWithTypography } from '../../../../components/DialogTitleWithTypography';
import { InlineLoader } from '../../../../components/Loader';
import { useUnsafeTranslation } from '../../../../i18next';
import { FlexContainer } from '../../../../layout/Flex';
import {
  matchUserBySearchTerm,
  toFullName,
  toSearchTermRegexp,
  useUsersByOrgId
} from '../../../../services/user';

const ResultsContainer = styled('div')((p) => ({
  marginTop: p.theme.spacing(2),
  maxHeight: 320,
  overflowY: 'auto'
}));

const EmptyHint = styled(Typography)((p) => ({
  display: 'block',
  textAlign: 'center',
  color: p.theme.custom.colors.gray.w500,
  padding: `${p.theme.spacing(5)}px 0`
}));

const ResultRow = styled('button')<{ selected: boolean }>((p) => ({
  display: 'block',
  width: '100%',
  textAlign: 'left',
  border: 'none',
  cursor: 'pointer',
  background: p.selected ? p.theme.custom.colors.blue.w100 : 'transparent',
  borderRadius: p.theme.custom.border.radius(),
  padding: p.theme.spacing(1.5),
  outline: p.selected ? `1px solid ${p.theme.custom.colors.blue.w500}` : 'none',
  ':hover': {
    background: p.selected
      ? p.theme.custom.colors.blue.w100
      : p.theme.custom.colors.gray.w100
  }
}));

const ResultCaption = styled(Typography)((p) => ({
  color: p.theme.custom.colors.gray.w500
}));

export const SelectEmployeeModal = ({
  open,
  onClose,
  orgId,
  fromUser,
  onContinue
}: {
  open: boolean;
  onClose: () => void;
  orgId: string;
  /** The profile the merge was started from — excluded from the results. */
  fromUser: Doc<IUser>;
  onContinue: (otherUserId: string) => void;
}) => {
  const { t } = useUnsafeTranslation();
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const [users, loading] = useUsersByOrgId(orgId);

  // Exclude the record the merge was started from — you can't merge someone
  // with themselves.
  const candidates = useMemo(
    () =>
      orderBy(
        (users || []).filter((u) => u.id !== fromUser.id),
        [(u) => u.data.lastName, (u) => u.data.preferredName],
        'asc'
      ),
    [users, fromUser.id]
  );

  const results = useMemo(() => {
    const trimmed = query.trim();
    if (!trimmed) {
      return [];
    }
    // Search by NAME only.
    const re = toSearchTermRegexp(trimmed);
    return candidates.filter((u) => matchUserBySearchTerm(re, u));
  }, [candidates, query]);

  const fromName = toFullName(fromUser.data) || t('this employee');

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitleWithTypography>
        {t('Merge {{ name }} with…', { name: fromName })}
      </DialogTitleWithTypography>
      <DialogContent>
        <Typography variant="body2">
          {t(
            'Choose one employee. Both records become a single profile — you’ll decide what to keep next.'
          )}
        </Typography>

        <TextField
          autoFocus
          fullWidth
          variant="outlined"
          margin="normal"
          placeholder={t('Search employees')}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <Search
                size={18}
                style={{ marginRight: 8, opacity: 0.6, flexShrink: 0 }}
              />
            )
          }}
        />

        <ResultsContainer>
          {loading && (
            <FlexContainer justifyContent="center" style={{ padding: 24 }}>
              <InlineLoader />
            </FlexContainer>
          )}
          {!loading && !query.trim() && (
            <EmptyHint variant="caption">
              {t('Search by name to find the employee to merge with.')}
            </EmptyHint>
          )}
          {!loading && !!query.trim() && !results.length && (
            <EmptyHint variant="caption">
              {t('No employees match “{{ query }}”.', { query: query.trim() })}
            </EmptyHint>
          )}
          {results.map((u) => (
            <ResultRow
              key={u.id}
              type="button"
              selected={selectedId === u.id}
              onClick={() => setSelectedId(u.id)}
            >
              <Typography variant="body1">{toFullName(u.data)}</Typography>
              <ResultCaption variant="caption">
                {[u.data.email, u.data.role, u.data.department]
                  .filter((x) => x)
                  .join(' • ')}
              </ResultCaption>
            </ResultRow>
          ))}
        </ResultsContainer>
      </DialogContent>
      <DialogActions>
        <Button color="primary" onClick={onClose}>
          {t('Cancel')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={!selectedId}
          onClick={() => selectedId && onContinue(selectedId)}
        >
          {t('Continue')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
